import { forwardRef, memo, PropsWithChildren, useEffect } from 'react'
import { Banner, Button, Heading } from '@boltenergy-be/design-system'
import styles from './PageLayout.module.scss'
import { PageLayoutProps } from './types.ts'
import { useLocation } from 'react-router'
import SubNav from 'components/subnav/SubNav.tsx'
import SeoTags from 'components/seo-tags/SeoTags.tsx'
import { checkIsDown, logout } from 'utils/app.ts'
import classNames from 'classnames'
import FaqButton from 'components/faq-button/FaqButton.tsx'
import { useNavigate } from 'react-router'
import AddressSwitcherBar from 'components/address-switcher-bar/AddressSwitcherBar.tsx'
import parse from 'html-react-parser'
import { useStoreSelector } from 'hooks/store.ts'
import { getUserTypeLoginPage } from 'utils/user.ts'
import { selectIsProducerAccount } from 'store/contact/slice.ts'

const PageLayout = memo(
  forwardRef<HTMLDivElement, PropsWithChildren<PageLayoutProps>>(
    (
      {
        description,
        tabs,
        children,
        title,
        faqCategories,
        classes,
        seoTitle,
        actions,
        backButtonUrl,
        openFaq,
        onFaqClose,
        withSwitcher,
        banner,
        tagsToHide,
        addressSwitcherClassName
      },
      ref
    ) => {
      // Redux
      const isProducer = useStoreSelector(selectIsProducerAccount)
      const { userType } = useStoreSelector((store) => store.auth)

      // React router
      const { pathname } = useLocation()
      const navigate = useNavigate()

      // Constants
      const { isDown, message, isApproaching } = checkIsDown()
      const activeTab = tabs?.length === 1 ? tabs[0] : tabs?.find((tab) => pathname.includes(tab.path))
      const currentFaqCategories = (isProducer ? faqCategories?.b2b : faqCategories?.b2c) || []

      useEffect(() => {
        if (isDown) {
          logout(() => void navigate(getUserTypeLoginPage(userType)))
        }
      }, [])

      return (
        <>
          <SeoTags
            title={
              activeTab ? `${seoTitle ?? (title as string)} - ${activeTab.seoTitle || activeTab.label}` : (seoTitle ?? (title as string))
            }
          />

          {isApproaching && (
            <Banner type="informative" className={styles.maintenance}>
              <p>{message}</p>
            </Banner>
          )}

          <header
            className={classNames(
              styles.header,
              { [styles['with-description']]: !!description || (tabs && tabs?.length > 1) },
              classes?.header
            )}
          >
            {backButtonUrl && (
              <Button leadingIcon="arrowLeft" variant="tertiary" className={styles.back} onClick={() => navigate(backButtonUrl)} />
            )}

            <div className={styles['title-wrapper']}>
              {typeof title === 'string' ? (
                <Heading as="h1" variant="h3" serif>
                  {title}
                </Heading>
              ) : (
                title
              )}
            </div>

            {(!!activeTab?.extraActions?.length || !!currentFaqCategories?.length || !!actions) && (
              <div className={styles.actions}>
                {actions}
                {activeTab?.extraActions?.length && activeTab.extraActions.map((action, index) => <div key={index}>{action}</div>)}
                {!!currentFaqCategories && (
                  <FaqButton
                    faqCategories={currentFaqCategories}
                    isOpen={openFaq}
                    onOpenChange={(open) => !open && onFaqClose?.()}
                    {...{ tagsToHide }}
                  />
                )}
              </div>
            )}
          </header>

          {tabs && tabs.length > 1 && (
            <SubNav
              className={styles['sub-nav']}
              wrapperClassName={styles['sub-nav-wrapper']}
              tabs={tabs.map(({ label, path, className, onClick }) => ({ label, path, className, onClick }))}
              tabRefs={Object.fromEntries(tabs.filter((tab) => tab.anchorRef).map((tab) => [tab.path, tab.anchorRef]))}
            />
          )}

          <div ref={ref} className={classNames(styles.content, classes?.body)}>
            {!!banner && (
              <Banner type={banner.type} className={classNames(banner.className)}>
                {typeof banner.message === 'string' ? <p>{parse(banner.message)}</p> : banner.message}
              </Banner>
            )}

            {!!withSwitcher && <AddressSwitcherBar className={classNames('mb-500', addressSwitcherClassName)} />}

            {!!description &&
              (typeof description === 'string' ? (
                <p className={styles.description}>{parse(description)}</p>
              ) : (
                <div className={styles.description}>{description}</div>
              ))}

            {children}
          </div>
        </>
      )
    }
  )
)

PageLayout.displayName = 'PageLayout'

export default PageLayout
